<template>
  <div class="mrl-analysis-result-container pa-2">
    <!-- Panel de Filtros -->
    <v-card class="mb-4 rounded-xl elevation-2 filter-panel" outlined>
      <v-card-text class="py-3">
        <v-row dense align="center">
          <!-- Filtro Campaña -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-campaign-area
              label="Campaña"
              v-model="filters.cmaId"
              typeArea="1"
              clearable
              noDefault
            />
          </v-col>

          <!-- Filtro Tipo Cultivo -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-definition
              label="Tipo Cultivo"
              :def="1173"
              v-model="filters.typeCultive"
              clearable
            />
          </v-col>

          <!-- Filtro Tipo de Producto -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-definition
              label="Tipo de Producto"
              :def="1740"
              v-model="filters.TypeSubstances"
              clearable
            />
          </v-col>

          <!-- Filtro Sustancia -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-substances
              label="Sustancia"
              :type-substances="filters.TypeSubstances"
              v-model="filters.asuId"
              clearable
              noDefault
            />
          </v-col>

          <!-- Filtro Mercado / Destino -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-definition
              label="Mercado / Destino"
              :def="1736"
              v-model="filters.typeMarket"
              clearable
            />
          </v-col>

          <!-- Filtro Interpretación (Todos / Aprobados / Restringidos) -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select
              label="Interpretación"
              :items="interpretationOptions"
              v-model="filters.interpretationStatus"
              clearable
            />
          </v-col>

          <!-- Filtro Sub Lote (Segunda Fila) -->
          <v-col cols="12" sm="6" md="3" lg="4">
            <s-select-lot
              label="Sub Lote"
              v-model="filters.fltId"
              clearable
              full
              noDefault
            />
          </v-col>

          <!-- Botones de Acción -->
          <v-col cols="12" sm="6" md="9" lg="8" class="d-flex justify-end align-center pt-2">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  outlined
                  height="40"
                  class="mr-2 px-3 font-weight-bold rounded-lg"
                  :loading="loading"
                  @click="loadData"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Consultar
                </v-btn>
              </template>
              <span>Consultar resultados de análisis vs LMR</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="success"
                  class="white--text px-3 font-weight-bold rounded-lg"
                  height="40"
                  elevation="1"
                  :disabled="matrixRows.length === 0"
                  @click="exportToExcel"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left>mdi-file-excel</v-icon>
                  Excel
                </v-btn>
              </template>
              <span>Exportar matriz de resultados a Excel (.xlsx)</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Resumen de KPIs / Métricas en Colores Pastel Ejecutivos -->
    <v-row dense class="mb-3">
      <!-- Total Evaluadas -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card kpi-card-blue rounded-xl" outlined elevation="0">
          <v-card-text class="d-flex align-center py-3">
            <v-avatar class="kpi-avatar-blue mr-3" size="44">
              <v-icon size="24" color="blue darken-2">mdi-flask-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold kpi-label-blue">Sustancias Evaluadas</div>
              <div class="text-h5 font-weight-black kpi-value">{{ filteredRows.length }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Aprobados (100% Verde) -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card kpi-card-green rounded-xl" outlined elevation="0">
          <v-card-text class="d-flex align-center py-3">
            <v-avatar class="kpi-avatar-green mr-3" size="44">
              <v-icon size="24" color="green darken-2">mdi-check-decagram</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold kpi-label-green">Aprobadas (100% LMR)</div>
              <div class="text-h5 font-weight-black kpi-value">{{ kpiCounts.approved }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Restringidos (>= 1 Mercado Excedido) -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card kpi-card-amber rounded-xl" outlined elevation="0">
          <v-card-text class="d-flex align-center py-3">
            <v-avatar class="kpi-avatar-amber mr-3" size="44">
              <v-icon size="24" color="orange darken-3">mdi-alert-octagon</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold kpi-label-amber">Restringidas (Excede LMR)</div>
              <div class="text-h5 font-weight-black kpi-value">{{ kpiCounts.restricted }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Total Mercados -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card kpi-card-teal rounded-xl" outlined elevation="0">
          <v-card-text class="d-flex align-center py-3">
            <v-avatar class="kpi-avatar-teal mr-3" size="44">
              <v-icon size="24" color="teal darken-2">mdi-earth</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold kpi-label-teal">Mercados / Países</div>
              <div class="text-h5 font-weight-black kpi-value">{{ marketColumns.length }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Matriz Grid de Resultados de Análisis -->
    <v-card class="rounded-xl elevation-1 table-card overflow-hidden" outlined>
      <v-card-title class="d-flex align-center py-3 px-4 bg-matrix-header flex-wrap">
        <div class="d-flex align-center mr-4">
          <v-icon color="primary" class="mr-2">mdi-chart-box-outline</v-icon>
          <span class="text-subtitle-1 font-weight-bold text-slate-800">
            EVALUACIÓN DE ANÁLISIS VS LÍMITES MÁXIMOS DE RESIDUOS (LMR)
          </span>
        </div>

        <!-- Leyenda Visual Pastel -->
        <div class="d-flex align-center flex-wrap gap-2 my-1">
          <v-chip x-small class="mr-2 px-2 font-weight-bold legend-chip-green" outlined>
            <v-icon x-small left color="green darken-2">mdi-check-circle</v-icon>
            Cumple LMR
          </v-chip>
          <v-chip x-small class="mr-2 px-2 font-weight-bold legend-chip-red" outlined>
            <v-icon x-small left color="red darken-2">mdi-close-circle</v-icon>
            Excede LMR (No Cumple)
          </v-chip>
          <v-chip x-small class="mr-2 px-2 font-weight-medium legend-chip-grey" outlined>
            <v-icon x-small left color="grey darken-1">mdi-minus</v-icon>
            Sin LMR / N.A.
          </v-chip>
        </div>

        <v-spacer></v-spacer>
        <span class="text-caption grey--text text--darken-1 font-italic">
          {{ filteredRows.length }} registros | {{ marketColumns.length }} mercados
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <!-- Tabla con Scroll Horizontal y Columnas Fijas -->
      <div class="matrix-table-wrapper">
        <v-data-table
          :key="tableKey"
          :headers="computedHeaders"
          :items="filteredRows"
          :loading="loading"
          dense
          class="matrix-custom-table elevation-0"
          :items-per-page="50"
          :footer-props="{
            'items-per-page-options': [25, 50, 100, -1],
            'items-per-page-text': 'Filas por página:'
          }"
          no-data-text="No se encontraron datos para los filtros seleccionados"
          loading-text="Cargando resultados de matriz..."
        >
          <!-- Celda Sustancia Activa / Metal (Sticky 1) -->
          <template v-slot:item.ActiveSubstancesName="{ item }">
            <span class="substance-label text-truncate d-block" :title="item.ActiveSubstancesName">
              {{ item.ActiveSubstancesName || 'Sin Nombre' }}
            </span>
          </template>

          <!-- Celda Tipo de Sustancia (Sticky 2) -->
          <template v-slot:item.TypeSubstanceName="{ item }">
            <span
              v-if="item.TypeSubstanceName"
              class="substance-type-badge font-weight-bold text-uppercase"
              :class="getSubstanceTypeBadgeClass(item.TypeSubstanceName)"
            >
              {{ item.TypeSubstanceName }}
            </span>
            <span v-else class="grey--text">-</span>
          </template>

          <!-- Celda Interpretación (Sticky 3) -->
          <template v-slot:item.interpretation="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="status-pastel-badge font-weight-bold text-uppercase"
                  :class="getItemInterpretation(item).cssClass"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon x-small left :color="getItemInterpretation(item).iconColor">
                    {{ getItemInterpretation(item).icon }}
                  </v-icon>
                  {{ getItemInterpretation(item).label }}
                </span>
              </template>
              <div class="pa-1">
                <div class="font-weight-bold mb-1">{{ item.ActiveSubstancesName }}</div>
                <div v-if="getItemInterpretation(item).status === 'NO_CONFORME' || getItemInterpretation(item).status === 'RECHAZADO'">
                  <div class="red--text text--lighten-2 font-weight-bold">
                    <v-icon x-small color="red lighten-2">mdi-close-circle</v-icon>
                    Excede LMR en todos los mercados evaluados ({{ getItemInterpretation(item).failedMarkets.length }}):
                  </div>
                  <div class="text-caption ml-2 mb-1">{{ getItemInterpretation(item).failedMarkets.join(', ') }}</div>
                  <div class="grey--text text--lighten-1">Mercados aptos: Ninguno</div>
                </div>
                <div v-else-if="getItemInterpretation(item).status === 'RESTRINGIDO'">
                  <div class="orange--text text--lighten-2 font-weight-bold">
                    <v-icon x-small color="orange lighten-2">mdi-alert-circle</v-icon>
                    Excede LMR en ({{ getItemInterpretation(item).failedMarkets.length }}):
                  </div>
                  <div class="text-caption ml-2 mb-1">{{ getItemInterpretation(item).failedMarkets.join(', ') }}</div>
                  <div class="green--text text--lighten-2 font-weight-bold">
                    <v-icon x-small color="green lighten-2">mdi-check-circle</v-icon>
                    Cumple LMR en ({{ getItemInterpretation(item).approvedMarkets.length }}):
                  </div>
                  <div class="text-caption ml-2">{{ getItemInterpretation(item).approvedMarkets.join(', ') }}</div>
                </div>
                <div v-else-if="getItemInterpretation(item).status === 'APROBADO'">
                  <div class="green--text text--lighten-2 font-weight-bold">
                    <v-icon x-small color="green lighten-2">mdi-check-circle</v-icon>
                    Cumple con todos los mercados evaluados ({{ getItemInterpretation(item).approvedMarkets.length }}):
                  </div>
                  <div class="text-caption ml-2">{{ getItemInterpretation(item).approvedMarkets.join(', ') }}</div>
                </div>
                <div v-else class="grey--text text--lighten-1">
                  Sin LMRs configurados para comparar en los mercados consultados.
                </div>
              </div>
            </v-tooltip>
          </template>

          <!-- Celda Acciones / Dictamen Director -->
          <template v-slot:item.directorActions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="btn-pastel-action font-weight-bold text-none"
                  :class="getApprovalStatusInfo(item.TypeStatusApprovals).cssClass"
                  @click="openDirectorApprovalModal(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon x-small left :color="getApprovalStatusInfo(item.TypeStatusApprovals).iconColor">
                    {{ getApprovalStatusInfo(item.TypeStatusApprovals).icon }}
                  </v-icon>
                  {{ getApprovalStatusInfo(item.TypeStatusApprovals).label }}
                </button>
              </template>
              <div class="pa-1">
                <div class="font-weight-bold">Dictamen: {{ getApprovalStatusInfo(item.TypeStatusApprovals).label }}</div>
                <div v-if="item.DateApproval" class="text-caption">Fecha: {{ formatDate(item.DateApproval) }}</div>
                <div v-if="item.ObservationApproval" class="text-caption">Obs: {{ item.ObservationApproval }}</div>
                <div class="text-caption grey--text text--lighten-2 mt-1">Clic para registrar o editar dictamen</div>
              </div>
            </v-tooltip>
          </template>

          <!-- Celda Cultivo -->
          <template v-slot:item.TypeCultiveName="{ item }">
            <span class="grey--text text--darken-3 font-weight-medium">
              {{ item.TypeCultiveName || '-' }}
            </span>
          </template>

          <!-- Celda Campaña -->
          <template v-slot:item.CmaName="{ item }">
            <span class="font-weight-medium text-caption grey--text text--darken-3">
              {{ item.CmaName || '-' }}
            </span>
          </template>

          <!-- Celda Lote -->
          <template v-slot:item.FltID="{ item }">
            <span class="lote-pastel-chip font-weight-bold">
              {{ item.FltID || '-' }}
            </span>
          </template>

          <!-- Celda Fundo -->
          <template v-slot:item.FagName="{ item }">
            <span class="font-weight-medium text-truncate d-block" :title="item.FagName">
              {{ item.FagName || '-' }}
            </span>
          </template>

          <!-- Celda Productor -->
          <template v-slot:item.PrdCardName="{ item }">
            <span class="font-weight-medium text-truncate d-block" :title="item.PrdCardName">
              {{ item.PrdCardName || '-' }}
            </span>
          </template>

          <!-- Celda Análisis -->
          <template v-slot:item.Analisis="{ item }">
            <span class="font-weight-medium grey--text text--darken-2">
              {{ item.Analisis || item.RmnID || '-' }}
            </span>
          </template>

          <!-- Celda Fecha Análisis -->
          <template v-slot:item.SampleSubmissionDate="{ item }">
            <span class="grey--text text--darken-3 font-weight-medium">
              {{ formatDate(item.SampleSubmissionDate) }}
            </span>
          </template>

          <!-- Celda Resultado Análisis -->
          <template v-slot:item.ResultadoAnalisis="{ item }">
            <span class="font-weight-medium">
              {{ formatDecimal(item.ResultadoAnalisis) }}
            </span>
          </template>

          <!-- Celda Incertidumbre -->
          <template v-slot:item.Incertidumbre="{ item }">
            <span class="grey--text text--darken-2">
              {{ formatDecimal(item.Incertidumbre) }}
            </span>
          </template>

          <!-- Celda Valor a Evaluar (Resaltada) -->
          <template v-slot:item.ValorEvaluar="{ item }">
            <span class="font-weight-bold primary--text">
              {{ formatDecimal(item.ValorEvaluar) }}
            </span>
          </template>

          <!-- Celdas Dinámicas de Países / Mercados -->
          <template
            v-for="mkt in marketColumns"
            v-slot:[`item.${mkt.key}`]="{ item }"
          >
            <div
              :key="mkt.key"
              class="d-flex justify-center align-center h-100"
            >
              <!-- Celda con LMR y Estado de Cumplimiento -->
              <v-tooltip bottom :key="`tip-${mkt.key}`">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-if="hasMarketData(item[mkt.key])"
                    class="market-cell-badge"
                    :class="getMarketCellClass(item[mkt.key])"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon
                      x-small
                      class="mr-1"
                      :color="isCompliant(item[mkt.key]) ? 'green darken-3' : 'red darken-3'"
                    >
                      {{ isCompliant(item[mkt.key]) ? 'mdi-check-bold' : 'mdi-close-thick' }}
                    </v-icon>
                    <span>{{ getLMRValue(item[mkt.key]) }}</span>
                  </div>
                  <!-- Sin LMR configurado -->
                  <span
                    v-else
                    class="grey--text text--lighten-1 font-weight-light"
                    v-bind="attrs"
                    v-on="on"
                  >
                    -
                  </span>
                </template>

                <!-- Tooltip Informativo -->
                <div>
                  <div class="font-weight-bold">{{ mkt.name }}</div>
                  <div v-if="hasMarketData(item[mkt.key])">
                    <div>LMR: <b>{{ getLMRValue(item[mkt.key]) }} ppm</b></div>
                    <div>Valor Evaluar: <b>{{ formatDecimal(item.ValorEvaluar) }} ppm</b></div>
                    <div>
                      Estado:
                      <b :class="isCompliant(item[mkt.key]) ? 'green--text' : 'red--text'">
                        {{ isCompliant(item[mkt.key]) ? 'CUMPLE CON LMR' : 'EXCEDE LMR (NO CUMPLE)' }}
                      </b>
                    </div>
                  </div>
                  <div v-else class="grey--text">Sin LMR configurado para este destino</div>
                </div>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <!-- Modal Dictamen / Aprobación del Director -->
    <v-dialog v-model="dialogDirectorApproval" max-width="720px" persistent>
      <v-card class="rounded-xl overflow-hidden modal-director-card">
        <!-- Cabecera con Gradiente Ejecutivo -->
        <v-card-title class="modal-header-gradient py-3 px-4 d-flex align-center">
          <v-avatar color="rgba(255,255,255,0.2)" size="36" class="mr-3">
            <v-icon color="white" size="20">mdi-gavel</v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold white--text line-height-1">
              Dictamen y Aprobación de Director
            </div>
            <div class="text-caption white--text opacity-80">
              Evaluación Técnica de LMRs vs Destinos Comerciales
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon dark small @click="dialogDirectorApproval = false" class="close-modal-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-5" v-if="selectedAnalysisItem">
          <!-- Resumen del Análisis y Lote -->
          <div class="info-summary-card pa-3 mb-4 rounded-xl">
            <v-row dense>
              <v-col cols="6" sm="3" class="info-summary-col">
                <div class="d-flex align-center text-caption font-weight-bold grey--text text--darken-2 mb-1">
                  <v-icon x-small color="blue darken-2" class="mr-1">mdi-map-marker-radius</v-icon>
                  LOTE / FUNDO
                </div>
                <div class="font-weight-bold text-body-2 text-truncate text-slate-800" :title="`${selectedAnalysisItem.FltID} - ${selectedAnalysisItem.FagName}`">
                  {{ selectedAnalysisItem.FltID || '-' }} - {{ selectedAnalysisItem.FagName || '-' }}
                </div>
              </v-col>

              <v-col cols="6" sm="3" class="info-summary-col">
                <div class="d-flex align-center text-caption font-weight-bold grey--text text--darken-2 mb-1">
                  <v-icon x-small color="indigo darken-2" class="mr-1">mdi-account-tie</v-icon>
                  PRODUCTOR
                </div>
                <div class="font-weight-bold text-body-2 text-truncate text-slate-800" :title="selectedAnalysisItem.PrdCardName">
                  {{ selectedAnalysisItem.PrdCardName || '-' }}
                </div>
              </v-col>

              <v-col cols="6" sm="3" class="info-summary-col">
                <div class="d-flex align-center text-caption font-weight-bold grey--text text--darken-2 mb-1">
                  <v-icon x-small color="teal darken-2" class="mr-1">mdi-flask-outline</v-icon>
                  ANÁLISIS / FECHA
                </div>
                <div class="font-weight-bold text-body-2 text-truncate text-blue-dark">
                  {{ selectedAnalysisItem.Analisis || selectedAnalysisItem.RmnID || '-' }}
                  <span class="grey--text text--darken-1 font-weight-medium">({{ formatDate(selectedAnalysisItem.SampleSubmissionDate) }})</span>
                </div>
              </v-col>

              <v-col cols="6" sm="3" class="info-summary-col">
                <div class="d-flex align-center text-caption font-weight-bold grey--text text--darken-2 mb-1">
                  <v-icon x-small color="green darken-2" class="mr-1">mdi-sprout</v-icon>
                  CAMPAÑA / CULTIVO
                </div>
                <div class="font-weight-bold text-body-2 text-truncate text-slate-800" :title="selectedAnalysisItem.CmaName">
                  {{ selectedAnalysisItem.CmaName || '-' }}
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Tabla Resumen de Sustancias del Análisis -->
          <div class="mb-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-caption text-uppercase font-weight-bold text-slate-700 letter-spacing-wide">
                <v-icon x-small color="primary" class="mr-1">mdi-format-list-checks</v-icon>
                Sustancias Evaluadas en este Análisis ({{ selectedAnalysisSubstances.length }}):
              </span>
              <span class="text-caption grey--text text--darken-1 font-italic">
                Evaluación individual por molécula
              </span>
            </div>

            <div class="modal-substances-wrapper rounded-lg overflow-hidden border">
              <v-simple-table dense class="modal-substances-table elevation-0">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left font-weight-bold py-2">Sustancia Activa</th>
                      <th class="text-center font-weight-bold py-2" style="width: 100px;">Tipo</th>
                      <th class="text-center font-weight-bold py-2" style="width: 85px;">Valor</th>
                      <th class="text-center font-weight-bold py-2" style="width: 120px;">Estado Sustancia</th>
                      <th class="text-left font-weight-bold py-2">Observación de Mercados</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(sub, idx) in selectedAnalysisSubstances" :key="`sub-${idx}`">
                      <td class="font-weight-bold text-caption text-slate-800">{{ sub.ActiveSubstancesName || '-' }}</td>
                      <td class="text-center">
                        <v-chip x-small :color="getSubstanceTypeColor(sub.TypeSubstanceName)" text-color="white" class="font-weight-bold text-uppercase px-2">
                          {{ sub.TypeSubstanceName || '-' }}
                        </v-chip>
                      </td>
                      <td class="text-center text-caption font-weight-black text-blue-primary">
                        {{ formatDecimal(sub.ValorEvaluar) }}
                      </td>
                      <td class="text-center">
                        <v-chip
                          x-small
                          :color="getItemInterpretation(sub).color"
                          text-color="white"
                          class="font-weight-bold px-2"
                        >
                          <v-icon x-small left>{{ getItemInterpretation(sub).icon }}</v-icon>
                          {{ getItemInterpretation(sub).label }}
                        </v-chip>
                      </td>
                      <td class="text-caption py-1">
                        <div v-if="getItemInterpretation(sub).status === 'NO_CONFORME' || getItemInterpretation(sub).status === 'RECHAZADO'" class="d-flex align-center flex-wrap">
                          <span class="market-pill-blocked-mini">
                            No conforme en ningún mercado: <b>{{ getItemInterpretation(sub).failedMarkets.join(', ') }}</b>
                          </span>
                        </div>
                        <div v-else-if="getItemInterpretation(sub).status === 'RESTRINGIDO'" class="d-flex align-center flex-wrap">
                          <span class="market-pill-blocked-mini">
                            Excede en: <b>{{ getItemInterpretation(sub).failedMarkets.join(', ') }}</b>
                          </span>
                        </div>
                        <div v-else-if="getItemInterpretation(sub).status === 'APROBADO'" class="d-flex align-center flex-wrap">
                          <span
                            class="market-pill-allowed-mini"
                            :title="`Mercados conformes: ${getItemInterpretation(sub).approvedMarkets.join(', ')}`"
                          >
                            <v-icon x-small color="green darken-3" class="mr-1">mdi-check</v-icon>
                            Cumple {{ getItemInterpretation(sub).approvedMarkets.length }} mercados
                          </span>
                        </div>
                        <span v-else class="grey--text font-italic">Sin LMR configurado</span>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </div>

          <!-- Banner Diagnóstico Automático Consolidado (Alto Contraste y Legibilidad) -->
          <div class="mb-4">
            <div class="text-caption text-uppercase font-weight-bold text-slate-700 letter-spacing-wide mb-1">
              <v-icon x-small color="primary" class="mr-1">mdi-chart-bell-curve</v-icon>
              Diagnóstico General del Análisis (Consolidado):
            </div>

            <!-- Banner Alerta Restringido / No Conforme (Alto Contraste) -->
            <div
              v-if="!consolidatedAnalysisDiagnosis.isApproved"
              :class="consolidatedAnalysisDiagnosis.allApprovedMarkets.length === 0 ? 'diagnostic-banner-rejected' : 'diagnostic-banner-restricted'"
              class="pa-3 rounded-xl"
            >
              <div class="d-flex align-center mb-2">
                <v-avatar :color="consolidatedAnalysisDiagnosis.allApprovedMarkets.length === 0 ? '#dc2626' : '#f97316'" size="28" class="mr-2 elevation-1">
                  <v-icon color="white" size="18">{{ consolidatedAnalysisDiagnosis.allApprovedMarkets.length === 0 ? 'mdi-close-circle' : 'mdi-alert-octagon' }}</v-icon>
                </v-avatar>
                <div class="text-subtitle-2 font-weight-black" :class="consolidatedAnalysisDiagnosis.allApprovedMarkets.length === 0 ? 'banner-rejected-title' : 'banner-restricted-title'">
                  {{ consolidatedAnalysisDiagnosis.allApprovedMarkets.length === 0 ? `ANÁLISIS NO CONFORME: PRESENTA ${consolidatedAnalysisDiagnosis.failedSubstancesCount} SUSTANCIA(S) QUE EXCEDEN LMR (SIN MERCADOS APTOS)` : `ANÁLISIS RESTRINGIDO: PRESENTA ${consolidatedAnalysisDiagnosis.failedSubstancesCount} SUSTANCIA(S) QUE EXCEDEN LMR` }}
                </div>
              </div>

              <!-- Mercados Bloqueados -->
              <div v-if="consolidatedAnalysisDiagnosis.allFailedMarkets.length > 0" class="mb-2 pl-1">
                <div class="text-caption font-weight-bold text-red-dark mb-1 d-flex align-center">
                  <v-icon x-small color="red darken-3" class="mr-1">mdi-close-circle</v-icon>
                  MERCADOS NO CONFORMES (BLOQUEADOS):
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <span
                    v-for="mkt in consolidatedAnalysisDiagnosis.allFailedMarkets"
                    :key="`fail-${mkt}`"
                    class="market-pill-blocked"
                  >
                    {{ mkt }}
                  </span>
                </div>
              </div>

              <!-- Mercados Aptos -->
              <div v-if="consolidatedAnalysisDiagnosis.allApprovedMarkets.length > 0" class="pl-1">
                <div class="text-caption font-weight-bold text-green-dark mb-1 d-flex align-center">
                  <v-icon x-small color="green darken-3" class="mr-1">mdi-check-circle</v-icon>
                  MERCADOS CONFORMES (APTOS PARA EXPORTACIÓN):
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <span
                    v-for="mkt in consolidatedAnalysisDiagnosis.allApprovedMarkets"
                    :key="`app-${mkt}`"
                    class="market-pill-allowed"
                  >
                    {{ mkt }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Banner Alerta Aprobado 100% (Verde Esmeralda de Alto Contraste) -->
            <div
              v-else
              class="diagnostic-banner-approved pa-3 rounded-xl"
            >
              <div class="d-flex align-center mb-2">
                <v-avatar color="#16a34a" size="28" class="mr-2 elevation-1">
                  <v-icon color="white" size="18">mdi-check-decagram</v-icon>
                </v-avatar>
                <div class="text-subtitle-2 font-weight-black banner-approved-title">
                  100% DE CUMPLIMIENTO: TODAS LAS SUSTANCIAS CUMPLEN LOS LMRs EVALUADOS
                </div>
              </div>
              <div class="text-caption text-green-dark pl-1 mb-2">
                Este lote está habilitado para libre comercialización y exportación a los siguientes destinos evaluados:
              </div>

              <!-- Países / Mercados Habilitados -->
              <div v-if="consolidatedAnalysisDiagnosis.allApprovedMarkets.length > 0" class="pl-1">
                <div class="text-caption font-weight-bold text-green-dark mb-1 d-flex align-center">
                  <v-icon x-small color="green darken-3" class="mr-1">mdi-check-circle</v-icon>
                  PAÍSES / DESTINOS HABILITADOS ({{ consolidatedAnalysisDiagnosis.allApprovedMarkets.length }}):
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <span
                    v-for="mkt in consolidatedAnalysisDiagnosis.allApprovedMarkets"
                    :key="`app-full-${mkt}`"
                    class="market-pill-allowed"
                  >
                    <v-icon x-small color="green darken-3" class="mr-1">mdi-check</v-icon>
                    {{ mkt }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Decisión del Director con Tarjetas Segmentadas -->
          <div class="mb-4">
            <div class="text-caption text-uppercase font-weight-bold text-slate-700 letter-spacing-wide mb-2">
              <v-icon x-small color="primary" class="mr-1">mdi-vote</v-icon>
              Decisión del Director para este Análisis:
            </div>
            <v-radio-group v-model="directorForm.status" row mandatory class="mt-0 decision-radio-group">
              <v-radio
                label="APROBADO"
                :value="2"
                color="green darken-2"
                class="decision-radio-option radio-green mr-3"
              ></v-radio>
              <v-radio
                label="RESTRINGIDO"
                :value="3"
                color="orange darken-3"
                class="decision-radio-option radio-orange mr-3"
              ></v-radio>
              <v-radio
                label="NO CONFORME"
                :value="4"
                color="red darken-2"
                class="decision-radio-option radio-red"
              ></v-radio>
            </v-radio-group>
          </div>

          <!-- Observación / Justificación Técnica -->
          <div>
            <div class="text-caption text-uppercase font-weight-bold text-slate-700 letter-spacing-wide mb-1">
              <v-icon x-small color="primary" class="mr-1">mdi-comment-text-outline</v-icon>
              Observación / Justificación Técnica del Dictamen:
            </div>
            <v-textarea
              v-model="directorForm.observation"
              outlined
              dense
              rows="3"
              counter="500"
              class="modal-observation-textarea rounded-lg"
              placeholder="Ej: Se autoriza cosecha y exportación únicamente para mercado Europeo y Asia. Bloqueado para USA..."
              hide-details="auto"
            ></v-textarea>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4 modal-actions-footer bg-slate-50">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey darken-2"
            class="px-4 font-weight-medium rounded-lg text-none"
            @click="dialogDirectorApproval = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            class="px-5 font-weight-bold rounded-lg text-none elevation-2 confirm-btn"
            :loading="savingApproval"
            @click="saveDirectorApproval"
          >
            <v-icon left size="18">mdi-check-bold</v-icon>
            Confirmar Dictamen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _sMRLsService from "@/services/Technicalassistance/MRLsService.js";
import _sRawAnalysisService from "@/services/Technicalassistance/TasRawMaterialAnalysisService.js";
import SSelectCampaignArea from "@/components/Utils/SSelectCampaignArea.vue";
import SSelectLot from "@/components/HarvestSchedule/SSelectLot.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import SSelectSubstances from "@/components/TecnicalAssistence/Substances/SSelectSubstances.vue";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export default {
  name: "MRLAnalysisResult",
  components: {
    SSelectCampaignArea,
    SSelectLot,
    SSelectDefinition,
    SSelectSubstances,
  },
  data: () => ({
    loading: false,
    tableKey: 1,
    search: "",
    debounceTimer: null,
    filters: {
      cmaId: null,
      fltId: null,
      typeCultive: null,
      TypeSubstances: null,
      asuId: null,
      typeMarket: null,
      interpretationStatus: null,
    },
    interpretationOptions: [
      { text: "Todos los Estados", value: null },
      { text: "APROBADO", value: "APROBADO" },
      { text: "RESTRINGIDO", value: "RESTRINGIDO" },
      { text: "NO CONFORME", value: "NO_CONFORME" },
      { text: "Sin LMR Configurado", value: "SIN_EVALUAR" },
    ],
    rawData: [],
    matrixRows: [],
    filteredRows: [],
    marketColumns: [],

    // Modal Director
    dialogDirectorApproval: false,
    selectedAnalysisItem: null,
    selectedAnalysisSubstances: [],
    savingApproval: false,
    directorForm: {
      status: 2,
      observation: "",
    },
  }),
  computed: {
    consolidatedAnalysisDiagnosis() {
      if (!this.selectedAnalysisSubstances || this.selectedAnalysisSubstances.length === 0) {
        return {
          isApproved: true,
          failedSubstancesCount: 0,
          allFailedMarkets: [],
          allApprovedMarkets: [],
          status: "APROBADO",
          suggestedStatusId: 2,
        };
      }

      let failedCount = 0;
      const failedMarketsSet = new Set();
      const allEvaluatedMarketsSet = new Set();

      this.selectedAnalysisSubstances.forEach((sub) => {
        const interp = this.getItemInterpretation(sub);

        // Registrar todos los mercados evaluados para esta molécula
        (interp.approvedMarkets || []).forEach((m) => allEvaluatedMarketsSet.add(m));

        // Si tiene mercados fallidos (o la sustancia es NO_CONFORME / RESTRINGIDO / RECHAZADO)
        if (interp.failedMarkets && interp.failedMarkets.length > 0) {
          interp.failedMarkets.forEach((m) => {
            allEvaluatedMarketsSet.add(m);
            failedMarketsSet.add(m); // Basta con que falle en cualquier sustancia para que el país quede bloqueado
          });
        }

        if (
          interp.status === "NO_CONFORME" ||
          interp.status === "RESTRINGIDO" ||
          interp.status === "RECHAZADO" ||
          (interp.failedMarkets && interp.failedMarkets.length > 0)
        ) {
          failedCount++;
        }
      });

      // Mercados aptos finales: SOLO aquellos evaluados que NUNCA fallaron en ninguna sustancia
      let finalApprovedMarkets = Array.from(allEvaluatedMarketsSet).filter(
        (m) => !failedMarketsSet.has(m)
      );

      // Si no hubo ningún fallo y allEvaluatedMarketsSet estuvo vacío pero existen mercados en la matriz
      if (
        failedCount === 0 &&
        failedMarketsSet.size === 0 &&
        finalApprovedMarkets.length === 0 &&
        this.marketColumns &&
        this.marketColumns.length > 0
      ) {
        finalApprovedMarkets = this.marketColumns.map((m) => m.name);
      }

      const allFailedMarkets = Array.from(failedMarketsSet);

      let status = "APROBADO";
      let suggestedStatusId = 2; // Aprobado

      if (failedCount > 0 || allFailedMarkets.length > 0) {
        if (finalApprovedMarkets.length === 0) {
          status = "NO_CONFORME";
          suggestedStatusId = 4; // No Conforme
        } else {
          status = "RESTRINGIDO";
          suggestedStatusId = 3; // Restringido
        }
      }

      return {
        isApproved: failedCount === 0 && allFailedMarkets.length === 0,
        failedSubstancesCount: failedCount,
        allFailedMarkets,
        allApprovedMarkets: finalApprovedMarkets,
        status,
        suggestedStatusId,
      };
    },

    kpiCounts() {
      let approved = 0;
      let restricted = 0;
      let nonConformant = 0;
      this.filteredRows.forEach((row) => {
        const res = this.getItemInterpretation(row);
        if (res.status === "APROBADO") approved++;
        else if (res.status === "RESTRINGIDO") restricted++;
        else if (res.status === "NO_CONFORME" || res.status === "RECHAZADO") nonConformant++;
      });
      return { approved, restricted, rejected: nonConformant };
    },

    computedHeaders() {
      const headers = [
        {
          text: "CAMPAÑA",
          value: "CmaName",
          align: "start",
          sortable: true,
          width: "140px",
        },
        {
          text: "LOTE",
          value: "FltID",
          align: "center",
          sortable: true,
          width: "90px",
        },
        {
          text: "FUNDO",
          value: "FagName",
          align: "start",
          sortable: true,
          width: "180px",
        },
        {
          text: "PRODUCTOR",
          value: "PrdCardName",
          align: "start",
          sortable: true,
          width: "180px",
        },
        {
          text: "ANALISIS",
          value: "Analisis",
          align: "start",
          sortable: true,
          width: "110px",
        },
        {
          text: "FECHA ANÁLISIS",
          value: "SampleSubmissionDate",
          align: "center",
          sortable: true,
          width: "120px",
        },
        {
          text: "SUSTANCIA ACTIVA / PRODUCTO",
          value: "ActiveSubstancesName",
          align: "start",
          sortable: true,
          width: "240px",
        },
        {
          text: "TIPO",
          value: "TypeSubstanceName",
          align: "center",
          sortable: true,
          width: "110px",
        },
        {
          text: "ESTADO SUSTANCIA",
          value: "interpretation",
          align: "center",
          sortable: true,
          width: "150px",
        },
        {
          text: "DICTAMEN ANÁLISIS",
          value: "directorActions",
          align: "center",
          sortable: false,
          width: "120px",
        },
        // {
        //   text: "CULTIVO",
        //   value: "TypeCultiveName",
        //   align: "center",
        //   sortable: true,
        //   width: "100px",
        // },
        {
          text: "RES. ANÁLISIS",
          value: "ResultadoAnalisis",
          align: "end",
          sortable: true,
          width: "115px",
        },
        {
          text: "INCERTIDUMBRE",
          value: "Incertidumbre",
          align: "end",
          sortable: true,
          width: "120px",
        },
        {
          text: "VALOR A EVALUAR",
          value: "ValorEvaluar",
          align: "end",
          sortable: true,
          width: "135px",
        },
      ];

      // Columnas dinámicas de países / mercados usando la key normalizada sin espacios
      this.marketColumns.forEach((mkt) => {
        headers.push({
          text: `LMR ${mkt.name.toUpperCase()}`,
          value: mkt.key,
          align: "center",
          sortable: true,
          width: "130px",
        });
      });

      return headers;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.applyLocalFilters();
        }, 250);
      },
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      clearTimeout(this.debounceTimer);
      this.loading = true;
      const requestID = this.$fun.getUserID();

      const filterObj = {
        CmaID: this.filters.cmaId && this.filters.cmaId !== 0 ? Number(this.filters.cmaId) : null,
        FltID: this.filters.fltId && this.filters.fltId !== 0 ? Number(this.filters.fltId) : null,
        fltId: this.filters.fltId && this.filters.fltId !== 0 ? Number(this.filters.fltId) : null,
        TypeSubstances: this.filters.TypeSubstances && this.filters.TypeSubstances !== 0 ? Number(this.filters.TypeSubstances) : null,
        AsuID: this.filters.asuId && this.filters.asuId !== 0 ? Number(this.filters.asuId) : null,
        TypeCultive: this.filters.typeCultive && this.filters.typeCultive !== 0 ? Number(this.filters.typeCultive) : null,
        TypeMarket: this.filters.typeMarket && this.filters.typeMarket !== 0 ? Number(this.filters.typeMarket) : null,
      };

      const params = {
        ...filterObj,
        filter: filterObj,
        draw: 1,
        start: 0,
        length: -1,
        order: [{ dir: "asc", column: "" }],
        searchText: this.search || "",
      };

      _sMRLsService
        .ResultadoMatriz(params, requestID)
        .then((response) => {
          if (response && response.data) {
            const items = Array.isArray(response.data)
              ? response.data
              : response.data.data || response.data.items || [];
            this.rawData = items;
            this.processMatrixData(items);
          } else {
            this.rawData = [];
            this.matrixRows = [];
            this.filteredRows = [];
            this.marketColumns = [];
            this.tableKey++;
          }
        })
        .catch((error) => {
          console.error("Error al cargar datos de ResultadoMatriz:", error);
          this.$fun.alert("Error al consultar resultados de matriz LMR", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    processMatrixData(items) {
      if (!items || items.length === 0) {
        this.matrixRows = [];
        this.filteredRows = [];
        this.marketColumns = [];
        this.tableKey++;
        return;
      }

      // Campos base del SP que no corresponden a mercados/países
      const fixedKeys = [
        "RmnID",
        "RmdID",
        "CmaID",
        "CmaName",
        "FltID",
        "FagName",
        "PrdCardName",
        "Analisis",
        "SampleSubmissionDate",
        "ActiveSubstancesName",
        "TypeCultiveName",
        "TypeSubstanceName",
        "ResultadoAnalisis",
        "Incertidumbre",
        "ValorEvaluar",
        "DocEntry",
        "TypeProduct",
        "TypeCultive",
        "SecStatus",
        "totalRecords",
        "RN",
        "id",
        "TypeStatusApprovals",
        "DateApproval",
        "UsrIDApproval",
        "ObservationApproval",
        "RmdStatus",
        "FailedMarkets",
        "ApprovedMarkets",
      ];

      // Extraer nombres de países / mercados a partir del primer registro
      const sample = items[0];
      const marketCols = [];
      let colIdx = 0;

      Object.keys(sample).forEach((key) => {
        if (!fixedKeys.includes(key)) {
          marketCols.push({
            key: `mkt_${colIdx++}`,
            originalKey: key,
            name: key,
          });
        }
      });

      this.marketColumns = marketCols;

      // Mapear filas asignando tanto los valores directos como los indexados
      this.matrixRows = items.map((item, index) => {
        const row = {
          ...item,
          id: item.RmdID || item.RmnID || index + 1,
        };
        marketCols.forEach((mkt) => {
          row[mkt.key] = item[mkt.originalKey];
        });
        return row;
      });

      this.applyLocalFilters();
      // Forzar reconstrucción de cabeceras y slots del datatable
      this.tableKey++;
    },

    applyLocalFilters() {
      let result = [...this.matrixRows];

      // Filtro por Sub Lote
      if (this.filters.fltId) {
        result = result.filter((row) => Number(row.FltID) === Number(this.filters.fltId));
      }

      // Filtro por Estado de Interpretación (Aprobado / Restringido / Sin evaluar)
      if (this.filters.interpretationStatus) {
        result = result.filter((row) => {
          const interp = this.getItemInterpretation(row);
          return interp.status === this.filters.interpretationStatus;
        });
      }

      // Filtro de texto de búsqueda
      if (this.search && this.search.trim()) {
        const query = this.search.trim().toLowerCase();
        result = result.filter(
          (row) =>
            (row.ActiveSubstancesName && row.ActiveSubstancesName.toLowerCase().includes(query)) ||
            (row.TypeCultiveName && row.TypeCultiveName.toLowerCase().includes(query)) ||
            (row.TypeSubstanceName && row.TypeSubstanceName.toLowerCase().includes(query)) ||
            (row.CmaName && row.CmaName.toLowerCase().includes(query)) ||
            (row.FltID && String(row.FltID).toLowerCase().includes(query)) ||
            (row.FagName && row.FagName.toLowerCase().includes(query)) ||
            (row.PrdCardName && row.PrdCardName.toLowerCase().includes(query))
        );
      }

      this.filteredRows = result;
    },

    getItemInterpretation(row) {
      if (!row || !this.marketColumns || this.marketColumns.length === 0) {
        return {
          status: "SIN_EVALUAR",
          label: "Sin LMR",
          color: "grey darken-1",
          cssClass: "chip-interp-none",
          iconColor: "grey darken-1",
          icon: "mdi-minus",
          failedMarkets: [],
          approvedMarkets: [],
        };
      }

      let evaluatedCount = 0;
      let failedCount = 0;
      const failedMarkets = [];
      const approvedMarkets = [];

      this.marketColumns.forEach((mkt) => {
        const rawVal = row[mkt.key];
        if (this.hasMarketData(rawVal)) {
          evaluatedCount++;
          if (this.isCompliant(rawVal)) {
            approvedMarkets.push(mkt.name);
          } else {
            failedCount++;
            failedMarkets.push(mkt.name);
          }
        }
      });

      if (evaluatedCount === 0) {
        return {
          status: "SIN_EVALUAR",
          label: "Sin LMR",
          color: "grey darken-1",
          cssClass: "chip-interp-none",
          iconColor: "grey darken-1",
          icon: "mdi-minus",
          failedMarkets: [],
          approvedMarkets: [],
        };
      }

      if (failedCount > 0) {
        if (approvedMarkets.length === 0) {
          // Ningún país está apto -> No Conforme
          return {
            status: "NO_CONFORME",
            label: "NO CONFORME",
            color: "red darken-3",
            cssClass: "chip-interp-rejected",
            iconColor: "red darken-3",
            icon: "mdi-close-circle",
            failedMarkets,
            approvedMarkets,
          };
        }

        // Con que falle un mercado pero tenga otros aptos -> Restringido
        return {
          status: "RESTRINGIDO",
          label: "RESTRINGIDO",
          color: "orange darken-3",
          cssClass: "chip-interp-restricted",
          iconColor: "orange darken-3",
          icon: "mdi-alert-octagon",
          failedMarkets,
          approvedMarkets,
        };
      }

      // 100% de mercados evaluados cumplen LMR -> Aprobado
      return {
        status: "APROBADO",
        label: "APROBADO",
        color: "green darken-2",
        cssClass: "chip-interp-approved",
        iconColor: "green darken-2",
        icon: "mdi-check-decagram",
        failedMarkets,
        approvedMarkets,
      };
    },

    getApprovalStatusInfo(status) {
      switch (Number(status)) {
        case 2:
          return { label: "APROBADO", color: "green darken-2", cssClass: "btn-status-approved", icon: "mdi-check-decagram", iconColor: "green darken-2" };
        case 3:
          return { label: "RESTRINGIDO", color: "orange darken-3", cssClass: "btn-status-restricted", icon: "mdi-shield-alert-outline", iconColor: "orange darken-3" };
        case 4:
          return { label: "NO CONFORME", color: "red darken-2", cssClass: "btn-status-rejected", icon: "mdi-close-circle", iconColor: "red darken-2" };
        default:
          return { label: "PENDIENTE", color: "grey darken-1", cssClass: "btn-status-pending", icon: "mdi-clock-outline", iconColor: "grey darken-2" };
      }
    },

    openDirectorApprovalModal(item) {
      this.selectedAnalysisItem = item;

      // Buscar TODOS los registros de sustancias que pertenecen a este mismo análisis
      const rmnId = item.RmnID;
      const analysisName = item.Analisis;
      const fltId = item.FltID;

      this.selectedAnalysisSubstances = this.matrixRows.filter((r) => {
        if (rmnId && r.RmnID) return r.RmnID === rmnId;
        if (analysisName && r.Analisis && fltId && r.FltID) {
          return r.Analisis === analysisName && r.FltID === fltId;
        }
        return r.id === item.id;
      });

      if (this.selectedAnalysisSubstances.length === 0) {
        this.selectedAnalysisSubstances = [item];
      }

      // Si ya tiene dictamen registrado previamente, precargar
      if (item.TypeStatusApprovals && Number(item.TypeStatusApprovals) > 1) {
        this.directorForm.status = Number(item.TypeStatusApprovals);
        this.directorForm.observation = item.ObservationApproval || "";
      } else {
        // Evaluar el diagnóstico general consolidado
        const diag = this.consolidatedAnalysisDiagnosis;
        this.directorForm.status = diag.suggestedStatusId;

        const analysisLabel = item.Analisis || (item.RmnID ? `Análisis #${item.RmnID}` : 'Análisis');
        const lotLabel = item.FltID ? `Lote ${item.FltID}` : '';

        if (diag.isApproved) {
          const mktList = diag.allApprovedMarkets && diag.allApprovedMarkets.length > 0
            ? `: ${diag.allApprovedMarkets.join(', ')}`
            : '';
          this.directorForm.observation = `${analysisLabel} (${lotLabel}) cumple satisfactoriamente con los Límites Máximos de Residuos (LMR) en todos los mercados evaluados${mktList}.`;
        } else if (diag.allApprovedMarkets.length === 0) {
          this.directorForm.observation = `No conforme para ${analysisLabel} (${lotLabel}). Supera el LMR y no cumple para ningún mercado evaluado (Bloqueados: ${diag.allFailedMarkets.join(', ')}).`;
        } else {
          this.directorForm.observation = `Aprobación restringida para ${analysisLabel} (${lotLabel}). Presenta sustancias no conformes para: ${diag.allFailedMarkets.join(', ')}. Mercados aptos: ${diag.allApprovedMarkets.join(', ')}.`;
        }
      }

      this.dialogDirectorApproval = true;
    },

    async saveDirectorApproval() {
      if (!this.selectedAnalysisItem || !this.selectedAnalysisItem.RmnID) {
        this.$fun.alert("No se encontró el ID del análisis a aprobar", "warning");
        return;
      }

      this.savingApproval = true;
      const rmnId = this.selectedAnalysisItem.RmnID;

      try {
        const detailsPayload = (this.selectedAnalysisSubstances || [])
          .filter((sub) => sub.RmdID && sub.RmdID > 0)
          .map((sub) => {
            const interp = this.getItemInterpretation(sub);
            let rmdStatus = 1; // 1: Pendiente
            if (interp.status === "APROBADO") {
              rmdStatus = 2; // 2: Aprobado
            } else if (interp.status === "RESTRINGIDO") {
              rmdStatus = 3; // 3: Restringido
            } else if (interp.status === "NO_CONFORME" || interp.status === "RECHAZADO") {
              rmdStatus = 4; // 4: No Conforme
            }

            return {
              RmdID: sub.RmdID,
              RmnID: rmnId,
              RmdStatus: rmdStatus,
              FailedMarkets: (interp.failedMarkets || []).join(", "),
              ApprovedMarkets: (interp.approvedMarkets || []).join(", "),
            };
          });

        const diag = this.consolidatedAnalysisDiagnosis;

        const payload = {
          RmnID: rmnId,
          TypeStatusApprovals: this.directorForm.status,
          ObservationApproval: this.directorForm.observation,
          FailedMarkets: (diag.allFailedMarkets || []).join(", "),
          ApprovedMarkets: (diag.allApprovedMarkets || []).join(", "),
          UsrIDApproval: this.$fun.getUserID(),
          DateApproval: new Date().toISOString(),
          UsrUpdateID: this.$fun.getUserID(),
          Details: detailsPayload,
        };

        const resp = await _sRawAnalysisService.saveDirectorApproval(payload, this.$fun.getUserID());
        if (resp && (resp.status === 200 || resp.data)) {
          this.$fun.alert("Dictamen del Director guardado correctamente para el Análisis y sus sustancias", "success");
          this.dialogDirectorApproval = false;
          this.loadData();
        } else {
          this.$fun.alert("Se registró el dictamen en el sistema", "success");
          this.dialogDirectorApproval = false;
        }
      } catch (err) {
        console.error("Error al guardar dictamen de director:", err);
        this.$fun.alert("Error al guardar el dictamen: " + (err.message || err), "error");
      } finally {
        this.savingApproval = false;
      }
    },

    hasMarketData(val) {
      if (val === null || val === undefined || val === "" || val === "NULL" || val === "null") return false;
      return typeof val === "string" && val.includes("|");
    },

    getLMRValue(val) {
      if (!this.hasMarketData(val)) return "-";
      const parts = val.split("|");
      const num = parseFloat(parts[0]);
      if (isNaN(num)) return parts[0];
      return parseFloat(num.toFixed(6));
    },

    isCompliant(val) {
      if (!this.hasMarketData(val)) return true;
      const parts = val.split("|");
      return parts[1] === "1" || parts[1] === 1;
    },

    getMarketCellClass(val) {
      if (!this.hasMarketData(val)) return "";
      return this.isCompliant(val) ? "market-cell-compliant" : "market-cell-alert";
    },

    formatDecimal(val) {
      if (val === null || val === undefined || val === "") return "-";
      const num = parseFloat(val);
      if (isNaN(num)) return val;
      return parseFloat(num.toFixed(6));
    },

    formatDate(val) {
      if (!val) return "-";
      if (typeof val === "string" && val.includes("T")) {
        return val.split("T")[0];
      }
      return val;
    },

    getSubstanceTypeColor(typeName) {
      if (!typeName) return "grey";
      const upper = typeName.toUpperCase();
      if (upper.includes("TRAZA")) return "cyan darken-2";
      if (upper.includes("SUSTANCIA")) return "indigo darken-1";
      if (upper.includes("METAL")) return "deep-orange darken-2";
      return "blue-grey";
    },

    getSubstanceTypeBadgeClass(typeName) {
      if (!typeName) return "chip-substance-default";
      const upper = typeName.toUpperCase();
      if (upper.includes("TRAZA")) return "chip-substance-traza";
      if (upper.includes("PESTICIDA") || upper.includes("SUSTANCIA")) return "chip-substance-pesticida";
      if (upper.includes("METAL")) return "chip-substance-metal";
      return "chip-substance-default";
    },

    async exportToExcel() {
      if (this.filteredRows.length === 0) {
        this.$fun.alert("No hay datos para exportar", "warning");
        return;
      }

      try {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = "SunExpert";
        workbook.created = new Date();

        const worksheet = workbook.addWorksheet("Resultado Matriz LMR");
        worksheet.pageSetup.orientation = "landscape";

        const baseColsCount = 13; // Se incluyen columnas base
        const totalCols = this.marketColumns.length + baseColsCount;
        const lastColLetter = this.getColLetter(totalCols);

        // Título Principal
        worksheet.mergeCells(`A1:${lastColLetter}1`);
        const titleCell = worksheet.getCell("A1");
        titleCell.value = "EVALUACIÓN DE ANÁLISIS VS LÍMITES MÁXIMOS DE RESIDUOS (LMR)";
        titleCell.font = { name: "Segoe UI", size: 13, bold: true, color: { argb: "FFFFFFFF" } };
        titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0D47A1" } };
        titleCell.alignment = { vertical: "middle", horizontal: "center" };
        worksheet.getRow(1).height = 32;

        // Encabezados
        const headerRowValues = [
          "CAMPAÑA",
          "LOTE",
          "FUNDO",
          "PRODUCTOR",
          "ANALISIS",
          "FECHA ANÁLISIS",
          "SUSTANCIA ACTIVA / PRODUCTO",
          "TIPO",
          "INTERPRETACIÓN",
          "CULTIVO",
          "RES. ANÁLISIS",
          "INCERTIDUMBRE",
          "VALOR A EVALUAR",
        ];

        this.marketColumns.forEach((mkt) => {
          headerRowValues.push(`LMR ${mkt.name.toUpperCase()}`);
        });

        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.height = 26;

        headerRow.eachCell((cell, colNumber) => {
          cell.font = { name: "Segoe UI", size: 9, bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: colNumber <= 6 ? "FF1565C0" : "FF1976D2" },
          };
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          cell.border = {
            top: { style: "thin", color: { argb: "FF90CAF9" } },
            left: { style: "thin", color: { argb: "FF90CAF9" } },
            bottom: { style: "medium", color: { argb: "FF0D47A1" } },
            right: { style: "thin", color: { argb: "FF90CAF9" } },
          };
        });

        // Filas de Datos
        this.filteredRows.forEach((row, index) => {
          const interp = this.getItemInterpretation(row);
          const rowValues = [
            row.CmaName || "",
            row.FltID || "",
            row.FagName || "",
            row.PrdCardName || "",
            row.Analisis || row.RmnID || "",
            this.formatDate(row.SampleSubmissionDate),
            row.ActiveSubstancesName || "",
            row.TypeSubstanceName || "",
            interp.label,
            row.TypeCultiveName || "",
            this.formatDecimal(row.ResultadoAnalisis),
            this.formatDecimal(row.Incertidumbre),
            this.formatDecimal(row.ValorEvaluar),
          ];

          this.marketColumns.forEach((mkt) => {
            const rawVal = row[mkt.key];
            if (this.hasMarketData(rawVal)) {
              rowValues.push(this.getLMRValue(rawVal));
            } else {
              rowValues.push("");
            }
          });

          const dataRow = worksheet.addRow(rowValues);
          dataRow.height = 22;

          dataRow.eachCell((cell, colNumber) => {
            const isBaseCol = colNumber <= baseColsCount;
            const marketIndex = colNumber - (baseColsCount + 1);
            const isEven = index % 2 === 0;

            let bgArgb = isEven ? "FFFFFFFF" : "FFF9FAFB";
            let fontColorArgb = "FF212121";

            // Columna Interpretación (Columna 9)
            if (colNumber === 9) {
              if (interp.status === "APROBADO") {
                bgArgb = "FFE8F5E9";
                fontColorArgb = "FF2E7D32";
              } else if (interp.status === "RESTRINGIDO") {
                bgArgb = "FFFFF3E0";
                fontColorArgb = "FFE65100";
              } else if (interp.status === "NO_CONFORME" || interp.status === "RECHAZADO") {
                bgArgb = "FFFFEBEE";
                fontColorArgb = "FFC62828";
              }
            }

            // Pintar colores para celdas de mercados (verde = cumple, rojo = no cumple)
            if (!isBaseCol && marketIndex >= 0 && marketIndex < this.marketColumns.length) {
              const mktKey = this.marketColumns[marketIndex].key;
              const rawVal = row[mktKey];
              if (this.hasMarketData(rawVal)) {
                if (this.isCompliant(rawVal)) {
                  bgArgb = "FFE8F5E9"; // Verde suave
                  fontColorArgb = "FF2E7D32";
                } else {
                  bgArgb = "FFFFEBEE"; // Rojo suave
                  fontColorArgb = "FFC62828";
                }
              }
            }

            cell.font = {
              name: "Segoe UI",
              size: 9,
              bold: colNumber === 1 || colNumber === 3 || colNumber === baseColsCount || (!isBaseCol && fontColorArgb !== "FF212121"),
              color: { argb: fontColorArgb },
            };

            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb } };
            cell.alignment = {
              vertical: "middle",
              horizontal: [1, 5, 7, 8].includes(colNumber) ? "left" : "center",
            };
            cell.border = {
              top: { style: "thin", color: { argb: "FFE2E8F0" } },
              left: { style: "thin", color: { argb: "FFE2E8F0" } },
              bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
              right: { style: "thin", color: { argb: "FFE2E8F0" } },
            };
          });
        });

        // Configurar Anchos de Columnas
        worksheet.columns.forEach((column, colIdx) => {
          if (colIdx === 0) column.width = 28; // Sustancia
          else if (colIdx === 1) column.width = 14; // Tipo
          else if (colIdx === 2) column.width = 16; // Interpretación
          else if (colIdx === 3) column.width = 12; // Cultivo
          else if (colIdx === 4) column.width = 18; // Campaña
          else if (colIdx === 5) column.width = 10; // Lote
          else if (colIdx === 6) column.width = 24; // Fundo
          else if (colIdx === 7) column.width = 26; // Productor
          else if (colIdx === 8) column.width = 14; // Fecha
          else if (colIdx === 9 || colIdx === 10) column.width = 14; // Res / Incert
          else if (colIdx === 11) column.width = 16; // Valor Evaluar
          else column.width = 15; // Mercados
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, `Resultado_Matriz_LMR_${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.$fun.alert("Resultados de matriz exportados exitosamente a Excel", "success");
      } catch (err) {
        console.error("Error al exportar a Excel:", err);
        this.$fun.alert("Error al exportar a Excel", "error");
      }
    },

    getColLetter(colIdx) {
      let temp, letter = "";
      while (colIdx > 0) {
        temp = (colIdx - 1) % 26;
        letter = String.fromCharCode(temp + 65) + letter;
        colIdx = (colIdx - temp - 1) / 26;
      }
      return letter || "A";
    },
  },
};
</script>

<style scoped>
.mrl-analysis-result-container {
  width: 100%;
}

.filter-panel {
  background: #ffffff;
}

/* =========================================================================
   TARJETAS KPI - ESTILO PASTEL EJECUTIVO PROFESIONAL
   ========================================================================= */
.kpi-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  border-width: 1px !important;
}
.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06) !important;
}

/* KPI 1: Azul Pastel (Sustancias Evaluadas) */
.kpi-card-blue {
  background-color: #f0f7ff !important;
  border-color: #bfdbfe !important;
}
.kpi-avatar-blue {
  background-color: #dbeafe !important;
}
.kpi-label-blue {
  color: #1e40af !important;
  letter-spacing: 0.5px;
}

/* KPI 2: Verde Pastel (Aprobadas 100% LMR) */
.kpi-card-green {
  background-color: #f0fdf4 !important;
  border-color: #bbf7d0 !important;
}
.kpi-avatar-green {
  background-color: #dcfce7 !important;
}
.kpi-label-green {
  color: #15803d !important;
  letter-spacing: 0.5px;
}

/* KPI 3: Ámbar / Melocotón Pastel (Restringidas) */
.kpi-card-amber {
  background-color: #fff7ed !important;
  border-color: #fed7aa !important;
}
.kpi-avatar-amber {
  background-color: #ffedd5 !important;
}
.kpi-label-amber {
  color: #c2410c !important;
  letter-spacing: 0.5px;
}

/* KPI 4: Teal Pastel (Mercados / Países) */
.kpi-card-teal {
  background-color: #f0fdfa !important;
  border-color: #99f6e4 !important;
}
.kpi-avatar-teal {
  background-color: #ccfbf1 !important;
}
.kpi-label-teal {
  color: #0f766e !important;
  letter-spacing: 0.5px;
}

.kpi-value {
  color: #0f172a !important;
  line-height: 1.1;
}

.bg-matrix-header {
  background-color: #f8fafc;
}

.bg-grey-lighten {
  background-color: #f8fafc;
}

/* =========================================================================
   CHIPS DE LEYENDA SUPERIOR
   ========================================================================= */
.legend-chip-green {
  background-color: #f0fdf4 !important;
  color: #166534 !important;
  border-color: #bbf7d0 !important;
}

.legend-chip-red {
  background-color: #fef2f2 !important;
  color: #991b1b !important;
  border-color: #fca5a5 !important;
}

.legend-chip-grey {
  background-color: #f8fafc !important;
  color: #64748b !important;
  border-color: #cbd5e1 !important;
}

/* =========================================================================
   TABLA MATRIZ CON ENCABEZADO PASTEL CORPORATIVO
   ========================================================================= */
.matrix-table-wrapper {
  overflow-x: auto;
  max-width: 100%;
}

.matrix-custom-table ::v-deep table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

.matrix-custom-table ::v-deep .v-data-table__wrapper {
  max-height: calc(100vh - 340px) !important;
  overflow-y: auto !important;
}

/* Encabezados Azul Bebé con Letras Negras */
.matrix-custom-table ::v-deep thead th {
  position: sticky !important;
  top: 0 !important;
  z-index: 3 !important;
  background: #dbeafe !important;
  color: #000000 !important;
  font-weight: 800 !important;
  font-size: 0.76rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  height: 40px !important;
  border-right: 1px solid #bfdbfe !important;
  border-bottom: 2px solid #93c5fd !important;
  padding: 4px 8px !important;
}

.matrix-custom-table ::v-deep tbody tr:hover td {
  background-color: #f8fafc !important;
}

/* Celdas de datos */
.matrix-custom-table ::v-deep tbody td {
  border-right: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.82rem;
  padding: 4px 8px !important;
  height: 40px !important;
}

.matrix-custom-table ::v-deep tbody tr:hover td {
  background-color: #f8fafc;
}

.substance-label {
  font-weight: 600;
  color: #0f172a;
}

/* =========================================================================
   BADGES Y CHIPS PASTEL PROFESIONALES
   ========================================================================= */

/* Tipo de Sustancia */
.substance-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.72rem;
  letter-spacing: 0.3px;
}

.chip-substance-traza {
  background-color: #ecfeff;
  color: #0e7490;
  border: 1px solid #a5f3fc;
}

.chip-substance-pesticida {
  background-color: #f1f5f9;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.chip-substance-metal {
  background-color: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
}

.chip-substance-default {
  background-color: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

/* Estado Sustancia / Interpretación */
.status-pastel-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  min-width: 110px;
}

.chip-interp-approved {
  background-color: #dcfce7;
  color: #15803d;
  border: 1px solid #86efac;
}

.chip-interp-restricted {
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
}

.chip-interp-rejected {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.chip-interp-none {
  background-color: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
}

/* Botón Dictamen Director */
.btn-pastel-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.76rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  outline: none;
}
.btn-pastel-action:hover {
  transform: translateY(-1px);
  filter: brightness(0.95);
}

.btn-status-pending {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.btn-status-approved {
  background-color: #dcfce7;
  color: #166534;
  border: 1px solid #86efac;
}

.btn-status-rejected {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.btn-status-restricted {
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
}

/* Chip Lote Pastel */
.lote-pastel-chip {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 5px;
  font-size: 0.75rem;
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

/* Badges de Celdas de Mercado */
.market-cell-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  min-width: 65px;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
}

.market-cell-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.market-cell-compliant {
  background-color: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.market-cell-alert {
  background-color: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

/* =========================================================================
   ESTILOS PREMIUM Y ALTO CONTRASTE PARA EL MODAL DEL DIRECTOR
   ========================================================================= */

.modal-director-card {
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.22) !important;
  background-color: #ffffff;
}

.modal-header-gradient {
  background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #2563eb 100%) !important;
  border-bottom: 2px solid #1e40af;
}

.close-modal-btn {
  transition: transform 0.15s ease-in-out;
}
.close-modal-btn:hover {
  transform: rotate(90deg);
}

.line-height-1 {
  line-height: 1.25 !important;
}

.opacity-80 {
  opacity: 0.85;
}

.letter-spacing-wide {
  letter-spacing: 0.5px;
}

/* Tarjeta Resumen Superior */
.info-summary-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
}

.info-summary-col {
  border-right: 1px dashed #cbd5e1;
}
.info-summary-col:last-child {
  border-right: none;
}

.text-slate-700 {
  color: #334155 !important;
}
.text-slate-800 {
  color: #1e293b !important;
}
.text-blue-dark {
  color: #1e40af !important;
}
.text-blue-primary {
  color: #2563eb !important;
}
.text-red-dark {
  color: #991b1b !important;
}
.text-green-dark {
  color: #166534 !important;
}

/* Tabla de Sustancias en Modal */
.modal-substances-wrapper {
  background-color: #ffffff;
}

.modal-substances-table ::v-deep table {
  border-collapse: collapse;
  width: 100%;
}

.modal-substances-table ::v-deep thead th {
  background: #f1f5f9 !important;
  color: #1e293b !important;
  font-size: 0.78rem !important;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 2px solid #cbd5e1 !important;
  height: 36px !important;
}

.modal-substances-table ::v-deep tbody td {
  border-bottom: 1px solid #f1f5f9;
  height: 38px !important;
  font-size: 0.82rem;
}

.modal-substances-table ::v-deep tbody tr:hover td {
  background-color: #f8fafc;
}

/* Mini Pills en la tabla de sustancias */
.market-pill-blocked-mini {
  background-color: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 0.75rem;
}

.market-pill-allowed-mini {
  background-color: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 0.75rem;
}

/* Banner Diagnóstico RESTRINGIDO (Alto Contraste y Calidez) */
.diagnostic-banner-restricted {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border: 1.5px solid #fdba74;
  border-left: 6px solid #ea580c;
  box-shadow: 0 2px 8px rgba(234, 88, 12, 0.08);
}

.banner-restricted-title {
  color: #9a3412 !important;
}

/* Banner Diagnóstico RECHAZADO / NO CONFORME (Rojo Intenso) */
.diagnostic-banner-rejected {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1.5px solid #fca5a5;
  border-left: 6px solid #dc2626;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.08);
}

.banner-rejected-title {
  color: #991b1b !important;
}

/* Banner Diagnóstico APROBADO (Verde Esmeralda) */
.diagnostic-banner-approved {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1.5px solid #86efac;
  border-left: 6px solid #16a34a;
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.08);
}

.banner-approved-title {
  color: #14532d !important;
}

/* Pills de Mercados (Etiquetas de Alto Contraste) */
.gap-1 {
  gap: 4px;
}

.market-pill-blocked {
  display: inline-block;
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 5px;
  box-shadow: 0 1px 2px rgba(185, 28, 28, 0.08);
}

.market-pill-allowed {
  display: inline-block;
  background-color: #dcfce7;
  color: #166534;
  border: 1px solid #86efac;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 5px;
  box-shadow: 0 1px 2px rgba(22, 101, 52, 0.08);
}

/* Opciones de Decisión */
.decision-radio-group ::v-deep .v-input--radio-group__input {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.decision-radio-option {
  padding: 6px 14px;
  border-radius: 10px;
  background-color: #f8fafc;
  border: 1.5px solid #e2e8f0;
  transition: all 0.2s ease-in-out;
}

.decision-radio-option:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.decision-radio-option.radio-green ::v-deep label {
  color: #166534 !important;
  font-weight: 700 !important;
  font-size: 0.88rem;
}

.decision-radio-option.radio-orange ::v-deep label {
  color: #c2410c !important;
  font-weight: 700 !important;
  font-size: 0.88rem;
}

.decision-radio-option.radio-red ::v-deep label {
  color: #b91c1c !important;
  font-weight: 700 !important;
  font-size: 0.88rem;
}

/* Textarea y Footer */
.modal-observation-textarea ::v-deep .v-input__slot {
  background-color: #ffffff !important;
}

.bg-slate-50 {
  background-color: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.confirm-btn {
  background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%) !important;
  color: #ffffff !important;
  letter-spacing: 0.3px;
  transition: all 0.15s ease-in-out;
}
.confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35) !important;
}
</style>
